import { Router } from "@frui.ts/screens";
import { bound } from "@frui.ts/helpers";
import type { PropertyName } from "@frui.ts/helpers";
import { attachAutomaticValidator } from "@frui.ts/validation";
import { computed } from "mobx";
import { Modules } from "config";
import BlacklistedEmailAddress from "entities/blacklistedEmailAddress";
import EmailBlacklistFilter, { EmailBlacklistFilterValidationRules } from "entities/filters/emailBlacklistFilter";
import EmailBlacklistRepository from "repositories/emailBlacklistRepository";
import ConfirmationService from "services/confirmationService";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService, { NotificationType } from "services/notificationService";
import SecurityService from "services/securityService";
import ListViewModel from "../../listViewModel";
import EmailBlacklistCreateViewModel from "./emailBlacklistCreateViewModel";
import EmailBlacklistDetailViewModel from "./emailBlacklistDetailViewModel";

const navigationName = "email-blacklist";

@Router.registerRoute({
  route: navigationName,
  name: Router.Self,
  children: [EmailBlacklistDetailViewModel, EmailBlacklistCreateViewModel],
})
export default class EmailBlacklistsViewModel extends ListViewModel<
  BlacklistedEmailAddress,
  EmailBlacklistDetailViewModel,
  EmailBlacklistCreateViewModel,
  EmailBlacklistFilter
> {
  readonly modelName = "emailBlacklist";

  constructor(
    protected createViewModelFactory: () => EmailBlacklistCreateViewModel,
    protected detailViewModelFactory: ReturnType<typeof EmailBlacklistDetailViewModel.Factory>,
    protected repository: EmailBlacklistRepository,
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    protected notificationService: NotificationService,
    protected confirmationService: ConfirmationService
  ) {
    super();
    this.navigationName = navigationName;
  }

  protected createFilter(): EmailBlacklistFilter {
    return attachAutomaticValidator(new EmailBlacklistFilter(), EmailBlacklistFilterValidationRules, false);
  }

  @computed
  get allowWrite() {
    return this.securityService.allowTo("charges.emailblacklist", "write");
  }

  @computed
  get allowRead() {
    return this.securityService.allowTo("charges.emailblacklist", "read");
  }

  @bound
  isItemActive(item: BlacklistedEmailAddress): boolean {
    if (!item.validUntil) {
      return true;
    }
    const validUntil = item.validUntil instanceof Date ? item.validUntil : new Date(item.validUntil);
    return validUntil > new Date();
  }

  @bound
  allowItemDetail(item: BlacklistedEmailAddress) {
    return item.permittedActions?.blacklistedEmailAddressDetailAccess?.isAllowed ?? true;
  }

  @bound
  allowItemDelete(item: BlacklistedEmailAddress) {
    return this.allowWrite && (item.permittedActions?.blacklistedEmailAddressChange?.isAllowed ?? false);
  }

  @bound
  allowItemEdit(item: BlacklistedEmailAddress) {
    return this.allowWrite && (item.permittedActions?.blacklistedEmailAddressChange?.isAllowed ?? false);
  }

  protected detailParams(_itemId: any): any[] {
    return [true]; // forceReadOnly = true for detail view
  }

  @bound
  onEditClick(item: BlacklistedEmailAddress) {
    const detailFactory = this.detailViewModelFactory;
    if (detailFactory) {
      this.tryActivateChild(detailFactory(item.id, false)); // forceReadOnly = false for edit
    }
  }

  @bound
  async onDeactivateClick(item: BlacklistedEmailAddress) {
    const confirmed = await this.confirmationService.showConfirmation(this.translate("deactivateConfirmation"), {
      confirmButton: { variant: "danger", text: this.translate("deactivate") },
    });

    if (confirmed) {
      await this.repository.deactivate(item.id);
      this.notificationService.add({
        title: this.translate("deactivated"),
        type: NotificationType.SUCCESS,
      });
      this.loadData();
    }
  }

  translateFilterAttribute(attribute: PropertyName<EmailBlacklistFilter>, size?: number) {
    return this.localizationService.translateAttribute("emailBlacklistFilter" as any, attribute, size);
  }

  // Override to prevent parsing string fields as nested objects (they may contain "&" character)
  protected parseFilterParam(paramName: keyof EmailBlacklistFilter | string, value: any) {
    const stringFields: Array<keyof EmailBlacklistFilter> = ["emailAddressSearchPattern", "description", "emailAddress"];
    if (stringFields.includes(paramName as keyof EmailBlacklistFilter)) {
      return value;
    }
    return super.parseFilterParam(paramName, value);
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.administration, `emailBlacklists.${code}`, size);
  }
}
